import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Images, 
  FolderOpen,
  Mail, 
  Settings, 
  LogOut, 
  Menu, 
  X,
  Moon,
  Sun,
  Shield,
  ShieldCheck,
  ShieldOff
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from './ui/button';
import { InputOTP, InputOTPGroup, InputOTPSlot } from './ui/input-otp';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { toast } from 'sonner';

function NavItem({ path, label, icon: Icon, isActive, onClick }) {
  return (
    <Link
      to={path}
      onClick={onClick}
      data-testid={`nav-${label.toLowerCase()}`}
      className={`
        flex items-center gap-3 px-4 py-3 rounded-lg
        transition-colors duration-200
        ${isActive ? 'bg-primary text-primary-foreground' : 'hover:bg-muted text-muted-foreground hover:text-foreground'}
      `}
    >
      <Icon className="h-5 w-5" />
      <span className="font-medium">{label}</span>
    </Link>
  );
}

export function AdminLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { admin, logout, setupMFA, enableMFA, disableMFA } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  const [mfaDialogOpen, setMfaDialogOpen] = useState(false);
  const [mfaSetupData, setMfaSetupData] = useState(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaLoading, setMfaLoading] = useState(false);
  const [disableMfaDialog, setDisableMfaDialog] = useState(false);
  const [disableMfaCode, setDisableMfaCode] = useState('');

  const isActive = (path) => location.pathname === path;
  const closeSidebar = () => setSidebarOpen(false);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
    toast.success('Logged out successfully');
  };

  const handleSetupMFA = async () => {
    setMfaLoading(true);
    try {
      const data = await setupMFA();
      setMfaSetupData(data);
      setMfaDialogOpen(true);
    } catch (error) {
      toast.error('Failed to setup MFA');
    } finally {
      setMfaLoading(false);
    }
  };

  const handleEnableMFA = async () => {
    if (mfaCode.length !== 6) return;
    setMfaLoading(true);
    try {
      await enableMFA(mfaCode);
      toast.success('MFA enabled successfully!');
      setMfaDialogOpen(false);
      setMfaCode('');
      setMfaSetupData(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code');
      setMfaCode('');
    } finally {
      setMfaLoading(false);
    }
  };

  const handleDisableMFA = async () => {
    if (disableMfaCode.length !== 6) return;
    setMfaLoading(true);
    try {
      await disableMFA(disableMfaCode);
      toast.success('MFA disabled successfully');
      setDisableMfaDialog(false);
      setDisableMfaCode('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code');
      setDisableMfaCode('');
    } finally {
      setMfaLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-background">
      <div className="noise-overlay" />
      
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={closeSidebar}
        />
      )}

      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 bg-card border-r border-border
        transform transition-transform duration-200
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-4 border-b border-border">
            <Link to="/admin/dashboard" className="font-heading text-xl font-semibold">
              Admin Panel
            </Link>
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={closeSidebar}>
              <X className="h-5 w-5" />
            </Button>
          </div>

          <nav className="flex-1 p-4 space-y-2">
            <NavItem path="/admin/dashboard" label="Dashboard" icon={LayoutDashboard} isActive={isActive('/admin/dashboard')} onClick={closeSidebar} />
            <NavItem path="/admin/images" label="Images" icon={Images} isActive={isActive('/admin/images')} onClick={closeSidebar} />
            <NavItem path="/admin/categories" label="Categories" icon={FolderOpen} isActive={isActive('/admin/categories')} onClick={closeSidebar} />
            <NavItem path="/admin/messages" label="Messages" icon={Mail} isActive={isActive('/admin/messages')} onClick={closeSidebar} />
            <NavItem path="/admin/settings" label="Settings" icon={Settings} isActive={isActive('/admin/settings')} onClick={closeSidebar} />
          </nav>

          <div className="p-4 border-t border-border space-y-4">
            <div className="flex items-center justify-between px-2">
              <span className="text-sm text-muted-foreground flex items-center gap-2">
                {admin?.mfa_enabled ? (
                  <><ShieldCheck className="h-4 w-4 text-green-500" />MFA Enabled</>
                ) : (
                  <><ShieldOff className="h-4 w-4 text-yellow-500" />MFA Disabled</>
                )}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={admin?.mfa_enabled ? () => setDisableMfaDialog(true) : handleSetupMFA}
                disabled={mfaLoading}
                data-testid="mfa-toggle"
              >
                {admin?.mfa_enabled ? 'Disable' : 'Enable'}
              </Button>
            </div>

            <div className="flex items-center justify-between px-2">
              <span className="text-sm text-muted-foreground">Theme</span>
              <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="admin-theme-toggle">
                {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </Button>
            </div>

            <Button
              variant="ghost"
              className="w-full justify-start text-muted-foreground hover:text-destructive"
              onClick={handleLogout}
              data-testid="logout-button"
            >
              <LogOut className="h-5 w-5 mr-3" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-h-screen">
        <header className="lg:hidden sticky top-0 z-30 bg-card border-b border-border p-4">
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(true)}>
              <Menu className="h-5 w-5" />
            </Button>
            <span className="font-heading text-lg font-semibold">Admin Panel</span>
            <Button variant="ghost" size="icon" onClick={toggleTheme}>
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
          </div>
        </header>

        <main className="flex-1 p-6 lg:p-8">
          {children}
        </main>
      </div>

      <Dialog open={mfaDialogOpen} onOpenChange={setMfaDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Setup Two-Factor Authentication
            </DialogTitle>
            <DialogDescription>
              Scan this QR code with your authenticator app
            </DialogDescription>
          </DialogHeader>
          
          {mfaSetupData && (
            <div className="space-y-6">
              <div className="flex justify-center p-4 bg-white rounded-lg">
                <img 
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(mfaSetupData.qr_uri)}`}
                  alt="MFA QR Code"
                  className="w-48 h-48"
                  data-testid="mfa-qr-code"
                />
              </div>
              
              <div className="text-center">
                <p className="text-xs text-muted-foreground mb-2">Or enter this code manually:</p>
                <code className="text-sm bg-muted px-3 py-1 rounded font-mono">
                  {mfaSetupData.secret}
                </code>
              </div>

              <div className="space-y-4">
                <p className="text-sm text-center text-muted-foreground">
                  Enter the 6-digit code from your authenticator app
                </p>
                <div className="flex justify-center">
                  <InputOTP maxLength={6} value={mfaCode} onChange={setMfaCode} data-testid="mfa-setup-code">
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                <Button onClick={handleEnableMFA} className="w-full" disabled={mfaLoading || mfaCode.length !== 6} data-testid="mfa-enable-submit">
                  {mfaLoading ? 'Enabling...' : 'Enable MFA'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={disableMfaDialog} onOpenChange={setDisableMfaDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldOff className="h-5 w-5 text-destructive" />
              Disable Two-Factor Authentication
            </DialogTitle>
            <DialogDescription>
              Enter your current MFA code to disable two-factor authentication.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex justify-center">
              <InputOTP maxLength={6} value={disableMfaCode} onChange={setDisableMfaCode} data-testid="mfa-disable-code">
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
            </div>
            <Button onClick={handleDisableMFA} variant="destructive" className="w-full" disabled={mfaLoading || disableMfaCode.length !== 6} data-testid="mfa-disable-submit">
              {mfaLoading ? 'Disabling...' : 'Disable MFA'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
